// M32 — Hook + context for the feedback widget. Lives outside
// FeedbackProvider.tsx so that file can satisfy react-refresh/only-
// export-components by exporting only the provider component.
//
// Usage from anywhere inside <FeedbackProvider>:
//   const { open } = useFeedback();
//   <Button onClick={() => open()}>Send feedback</Button>
//
// Pre-fill for the error-boundary path:
//   open({ kind: 'bug', prefillMessage: 'I was trying to…', errorContext })

import { createContext, useContext } from 'react';
import type { FeedbackKind } from '../lib/types';

export interface OpenFeedbackOptions {
  /** Pre-select a kind chip. Defaults to 'bug' when launched from an
   * error boundary, 'suggestion' otherwise. */
  kind?: FeedbackKind;
  /** Optional text to pre-populate the message field. The user can
   * still edit before submitting. */
  prefillMessage?: string;
  /** Optional error name + stack snippet, captured when launched from
   * the Sentry ErrorBoundary. Stored on the row's `error_context`
   * column; never shown back to the user. */
  errorContext?: string;
}

export interface FeedbackContextValue {
  open: (opts?: OpenFeedbackOptions) => void;
}

export const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useFeedback must be used within FeedbackProvider');
  return ctx;
}
